import { useConnectStore } from '@/libs/api/connect/connect.store.ts'
import type { Composer } from 'vue-i18n'
import { useFeatureFlagStore } from '@/stores/feature-flag.store.ts'
import { i18n } from '@/i18n/i18n.ts'
import type { SettingsLinexo } from '@/libs/api/connect/connect.types.ts'
import { useConnectService } from '@/libs/api/connect/connect.service.ts'
import { useGlobalErrorService } from '@/services/error.service.ts'
import { useInsuranceStore } from '@/libs/api/insurance/insurance.store.ts'
import { useInsuranceService } from '@/libs/api/insurance/insurance.service.ts'
import { useInsuranceApiClientImpl } from '@/client/insurance-client-impl.ts'

function createConnectServiceImpl(
  connectStore: ReturnType<typeof useConnectStore>,
  i18nInstance: Composer,
  featureFlagStore: ReturnType<typeof useFeatureFlagStore>,
  errorService: ReturnType<typeof useGlobalErrorService>,
  insuranceStore: ReturnType<typeof useInsuranceStore>,
  insuranceService: ReturnType<typeof useInsuranceService>
) {
  function initLinexoSettings(settings: SettingsLinexo) {
    // init design
    switch (settings.ctaVariant) {
      case 'boxed':
        featureFlagStore.enableDesign('design-v1')
        break
      default:
        featureFlagStore.enableDesign('design-v2')
        break
    }
  }

  return useConnectService(connectStore, i18nInstance, {
    onInitLinexoSettings: initLinexoSettings,
    onInitError(error) {
      errorService.handleError(error)
    },
    async onPostInit() {
      const defaultDeviceIds =
        connectStore.getSelectedDeviceClasses?.map((device) => device.id) ?? []

      const deviceClassIds = [...defaultDeviceIds, connectStore.batteryDeviceId]

      const mapping = await insuranceService.mapOffersToDeviceClass(deviceClassIds)

      insuranceStore.setOffers(mapping)
    }
  })
}

export const useConnectServiceImpl = () => {
  const insuranceClient = useInsuranceApiClientImpl()
  return createConnectServiceImpl(
    useConnectStore(),
    i18n.global,
    useFeatureFlagStore(),
    useGlobalErrorService(),
    useInsuranceStore(),
    useInsuranceService(insuranceClient)
  )
}
